/**
 * The two non-content states of a detail page (web plan W0.2), shared by the
 * promotion page and EntityDetailLayout (brand, shop, store).
 *
 *  - DetailNotFound: the page's H1 says the entity is not there, with real
 *    links onward. The page sets `noindex` through `detailSeoOptions`.
 *  - DetailLoadError: the load failed (429 / 5xx / network); a Retry button,
 *    and nothing that tells a crawler the entity is gone.
 */
import { SearchX } from 'lucide-react';
import { Button, ErrorState } from '@/components/ui';
import { RATE_LIMITED_MESSAGE } from '@/hooks/useAppData';
import { useCatalogPath } from '@/hooks/useRouteLocale';
import { DETAIL_TYPE_LABELS, type DetailKind } from '@/utils/detailStatus';

const NOT_FOUND_MESSAGES: Record<DetailKind, string> = {
  promotion: "This promotion isn't available. It may have ended or isn't published yet.",
  brand: "We couldn't find this brand.",
  shop: "We couldn't find this shop.",
  store: "We couldn't find this store.",
};

/**
 * Where a dead link leads next. Shops and stores point at lists that always
 * have content: `/shops` may be empty (and unlinked, W0.3), and there is no
 * store list.
 */
const BROWSE: Record<DetailKind, { to: string; label: string }> = {
  promotion: { to: '/promotions', label: 'Browse promotions' },
  brand: { to: '/brands', label: 'Browse brands' },
  shop: { to: '/promotions', label: 'Browse promotions' },
  store: { to: '/brands', label: 'Browse brands' },
};

export function DetailNotFound({ kind }: { kind: DetailKind }) {
  const browse = BROWSE[kind];
  // Catalog links: `/pk/en/…` once the URL move is live (web plan W2.1).
  const catalog = useCatalogPath();
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
        <SearchX size={40} className="text-primary" aria-hidden="true" />
      </div>
      <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-ink">
        {DETAIL_TYPE_LABELS[kind]} not found
      </h1>
      <p className="max-w-sm text-sm text-ink-secondary">{NOT_FOUND_MESSAGES[kind]}</p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
        <Button to={catalog(browse.to)}>{browse.label}</Button>
        <Button variant="outline" to={catalog('/')}>
          Go home
        </Button>
      </div>
    </div>
  );
}

export interface DetailLoadErrorProps {
  kind: DetailKind;
  /** `DetailState.errorCode`; RATE_LIMITED gets the "wait a moment" copy. */
  errorCode?: string | null;
  onRetry: () => void;
  onBack?: () => void;
}

export function DetailLoadError({ kind, errorCode, onRetry, onBack }: DetailLoadErrorProps) {
  return (
    <ErrorState
      titleAs="h1"
      title={`Couldn't load this ${kind}`}
      message={
        errorCode === 'RATE_LIMITED'
          ? RATE_LIMITED_MESSAGE
          : 'Something went wrong while loading it. Please try again.'
      }
      onRetry={onRetry}
      onBack={onBack}
    />
  );
}
